import * as React from 'react';
import styled from 'styled-components';
import InputContainer, {InputContainerProps} from 'containers/InputContainer';
import Input, {InputProps} from 'components/Input';
import Select, {OptionObject, SelectProps} from 'components/Select';
import {getCurrencyChar} from 'helpers/get-price-label';
import outline from 'helpers/outline';
import {Currency, PriceObject} from 'types';

export type SelectPriceProps = Omit<InputContainerProps, 'children'> & {
  defaultPrice?: PriceObject;
  inputProps?: InputProps;
  selectProps?: SelectProps;
  options?: OptionObject[];
  onChange?(price: Partial<PriceObject>): void;
}

const Container = styled.div`
  column-gap: 5px;
  grid-auto-flow: column;
  display: grid;
`;

const InputWrapper = styled.div`
  input:focus {
    ${outline(0, 1)};
  }
`;

export const CurrencyOptions: OptionObject[] = [
  {
    label: getCurrencyChar(Currency.RUB),
    value: Currency.RUB,
  },
  {
    label: getCurrencyChar(Currency.USD),
    value: Currency.USD,
  },
];

/**
 * The component render select price
 * @param {SelectPriceProps} props
 * @return {React.ReactElement<SelectPriceProps>}
 * @constructor
 */
export default function SelectPrice(
    props: SelectPriceProps,
): React.ReactElement<SelectPriceProps> {
  const inputContainerProps: Omit<InputContainerProps, 'children'> = {
    label: props.label,
    note: props.note,
    error: props.error,
    required: props.required,
  };

  /**
   * Render input amount
   * @return {JSX.Element}
   */
  function renderInput(): JSX.Element {
    /**
     * Handle change amount
     * @param {React.ChangeEvent<HTMLInputElement>} event
     */
    function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
      props.onChange && props.onChange({
        amount: Number(event.target.value),
        currency: props.defaultPrice?.currency,
      });
    }

    return (
      <InputWrapper>
        <Input
          type="number"
          defaultValue={props.defaultPrice?.amount}
          onChange={handleChange}
          placeholder="Цена"
          {...props.inputProps}
        />
      </InputWrapper>
    );
  }

  /**
   * Render select currency
   * @return {JSX.Element}
   */
  function renderSelect(): JSX.Element {
    /**
     * Get default value
     * @param {PriceObject} price
     * @return {OptionObject | undefined}
     */
    function getDefaultValue(price: PriceObject): OptionObject | undefined {
      return CurrencyOptions.find((currency) => (
        currency.value === price.currency
      ));
    }

    /**
     * Handle change currency
     * @param {OptionObject | null} option
     */
    function handleChange(option: OptionObject | null): void {
      option && props.onChange && props.onChange({
        amount: props.defaultPrice?.amount,
        currency: option.value as Currency,
      });
    }

    return (
      <Select
        placeholder="Валюта"
        defaultValue={props.defaultPrice && (
          getDefaultValue(props.defaultPrice)
        )}
        options={props.options || CurrencyOptions}
        onChange={handleChange}
        {...props.selectProps}
      />
    );
  }

  return (
    <InputContainer {...inputContainerProps}>
      <Container>
        {renderInput()}
        {renderSelect()}
      </Container>
    </InputContainer>
  );
}
